import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Box
} from "@mui/material";

import NavBar from "../../components/nav-bar/navBar";
import LogoutConfirmation from "../../components/popup-modal/logoutPopup";
import styles from "../dashboard/dashboard.module.scss";
import userIcon from "../../asserts/images/avatar1.png";
import logoutIcon from "../../asserts/images/logout.jpg";
import androidIcon from "../../asserts/images/android apk.png";
import appleIcon from "../../asserts/images/appleios.png";
import { authenticateActions } from "../../store/store";
import SubmissionStatusModal from "../../components/popup-modal/submitStatusModal";
import AdminAPI from "../../api/adminApi.mjs";
import NapidAuthAPI from "../../api/napidAuthApi.mjs";
import ClientAPI from "../../api/clientApi.mjs";

const MobileNumberupdationList = () => {
  const [selectedNav, setSelectedNav] = useState("reports");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(""); // For first dropdown
  const dispatch = useDispatch();
  const accessToken = useSelector((state) => state.auth.accessToken);
  const userEmail = useSelector((state) => state.auth.userEmailId);
  const location = useLocation();
  const { role } = location.state || {};
  const userRole = useSelector((state) => state.auth.userRole);
  const [applications, setApplications] = useState("");
  const [loading, setLoading] = useState(true);
  const [idValue, setIdValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [mobileNumberListData, setMobileNumberListData] = useState([]);
  const lastRowRef = useRef(null);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDownload, setSelectedDownload] = useState("");

  const totalPages = Math.ceil(mobileNumberListData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = mobileNumberListData.slice(startIndex, endIndex);

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleInputChange = (event) => {
    setIdValue(event.target.value);
  };

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    } else if (direction === "next" && currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const data = await ClientAPI.getrenewalApplication();
      if (
        data.message === "Invalid or expired access token" &&
        data.statusCode === "Failed"
      ) {
        handleConfirmLogout();
      }
      const appList = Array.isArray(data) ? data : [data];

      setApplications(appList);
    } catch (error) {
      console.error("Error fetching applications:", error);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirmLogout = async () => {
    setShowConfirmationModal(false);
    try {
      await NapidAuthAPI.logoutUser(accessToken);
      localStorage.removeItem("refreshToken");
      dispatch(authenticateActions.logout());
      localStorage.removeItem("auth");
      localStorage.removeItem("lastPage"); // Remove last visited page
      window.location.href = "/login"; //
    } catch (error) { }
  };

  const handleCancelLogout = () => {
    setShowConfirmationModal(false);
  };

  const handleAppSelect = (event) => {
    setSelectedApp(event.target.value);
    setMobileNumberListData([]);
    setIdValue("");
  };

  const handleDownload = () => {
    if (selectedDownload === "pdf") {
      handlePdfDownloadMobileNumber(); // Call PDF API
    } else if (selectedDownload === "excel") {
      handleExcelDownloadMobileNumber(); // Call Excel API
    } else {
      setModalOpen(true);
      setIsSuccess(false);
      setMessage("Select format to download");
    }
  };

  const handleSearch = async () => {

    if (!idValue) {
      setModalOpen(true);
      setIsSuccess(false);
      setMessage("Please enter a value to search.");
      return;
    }
    setIsSearchClicked(true);
    try {
      setLoading(true);
      const result = await AdminAPI.getMobilenumberUpdationList(
        selectedApp,
        idValue
      );

      setLoading(false);

      if (Array.isArray(result) && result.length > 0) {
        const formattedHistoryData = result.map((item) => ({
          digitalId: item.digitalId || "N/A",
          mobileNumber: item.combinedCountryCodeAndMobileNo || "N/A",
          customerStatus: item.customerStatus || "N/A",
          changedAt: item.createdAt || "N/A",
        }));

        setMobileNumberListData(formattedHistoryData);
      } else {
        setMobileNumberListData([]);
      }
    } catch (error) {
      setModalOpen(true);
      setIsSuccess(false);
      setMessage(error.message || "Failed to fetch data.");
      setLoading(false);
    }
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePdfDownloadMobileNumber = async () => {
    const userId = idValue;
    const serviceId = selectedApp;
    const response = await AdminAPI.getMobileNumberupdationPdf(serviceId, userId);

    if (response?.data?.type === "application/json") {
      const text = await response?.data?.text();
      const json = JSON.parse(text);
      const messageData = json.message;
      setIsSuccess(false);
      setMessage(messageData);
      setModalOpen(true);
      return;
    }
    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const href = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = href;
    const date = new Date();
    const formattedDate = date.toISOString().split('T')[0];
    link.setAttribute("download", `mobile-number-update-report-${formattedDate}.pdf`);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  const handleExcelDownloadMobileNumber = async () => {
    const userId = idValue;
    const serviceId = selectedApp;
    const response = await AdminAPI.getMobileNumberupdationExcel(serviceId, userId);
    if (response?.data?.type === "application/json") {
      const text = await response?.data?.text();
      const json = JSON.parse(text);
      const messageData = json.message;
      setIsSuccess(false);
      setMessage(messageData);
      setModalOpen(true);
      return;
    }
    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const href = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = href;
    const date = new Date();
    const formattedDate = date.toISOString().split('T')[0];
    link.setAttribute("download", `mobile-number-update-report-${formattedDate}.xlsx`);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  const handleNavClick = (navItem) => {
    setSelectedNav(navItem);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <div className={styles.dashboardContainer}>
      <NavBar role={role} onNavClick={handleNavClick} />
      <div className={styles.content}>
        <div className={styles.welcomeMessage}>Welcome {userRole},</div>
        <div className={styles.userInfo}>
          <img
            src={androidIcon}
            alt="Android Icon"
            className={styles.platformIcon}
          />
          <img
            src={appleIcon}
            alt="Apple Icon"
            className={styles.platformIcon}
          />
          <img src={userIcon} alt="User Icon" className={styles.userIcon} />
          <span className={styles.userEmail}>{userEmail}</span>
        </div>
        <Box
          sx={{
            backgroundColor: "white",
            padding: "10px",
            borderRadius: "8px",
            marginBottom: "4px",
            textAlign: "center",
            maxWidth: "100%",
            width: "calc(100% - <sideNavWidth>)",
            position: "relative",
            left: "<sideNavWidth>",
            marginTop: "50px",
          }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "16px",
              color: "grey",
              textAlign: "center",
              marginLeft: "0px",
            }}>
            On this page, you can search and view mobile number updation report
            by user Id.
          </Typography>
        </Box>
        <div className={styles.dropdownContainer}>
          <FormControl
            fullWidth
            variant="outlined"
            style={{
              marginTop: "30px",
              width: "230px", // Set input width here
              backgroundColor: "white",
            }}
          >
            <InputLabel id="app-select-label">Select Application</InputLabel>
            <Select
              labelId="app-select-label"
              id="app-select"
              value={selectedApp}
              onChange={handleAppSelect}
              label="Select Application"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                    width: 240,
                    overflowY: 'auto',
                    whiteSpace: 'normal',
                  },
                },
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null, // ensures correct vertical alignment
              }}
            >
              {applications.length > 0 ? (
                applications.map((app) => (
                  <MenuItem
                    key={app.id}
                    value={app.id}
                    style={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      lineHeight: '1.4',
                      paddingTop: '8px',
                      paddingBottom: '8px',
                    }}
                    title={app.name}
                  >
                    {app.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No applications available</MenuItem>
              )}
            </Select>
          </FormControl>
        </div>
        <div className={styles.inputContainer}>
          {selectedApp && (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "16px",
                margin: "20px auto",
                marginTop: "-75px",
                marginLeft: "-95px",
              }}>
              <TextField
                variant="outlined"
                label="User ID/Card Number"
                placeholder="Enter User ID/Card Number"
                value={idValue}
                onChange={handleInputChange}
                style={{
                  width: "200px",
                  backgroundColor: "white",
                }}
              />
              <Button
                variant="contained"
                color="primary"
                style={{
                  backgroundColor: "red",
                  color: "white",
                  padding: "10px 15px",
                  fontSize: "13px",
                  marginLeft: "12px",
                  marginTop: "0px",
                  height: '7vh'
                }}
                onClick={() => {
                  handleSearch();
                }}>
                SEARCH
              </Button>
            </div>
          )}
          {isSearchClicked && loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                top: "30px",
                alignItems: "center",
                height: "10vh",
                marginLeft: "-340px",
              }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {currentData && currentData.length > 0 && (
                <div
                  style={{
                    marginTop: "0px",
                    textAlign: "center",
                    marginLeft: "510px",
                  }}>
                  <label htmlFor="rows-per-page" style={{ marginRight: "5px" }}>
                    Select page size
                  </label>
                  <select
                    id="rows-per-page"
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    style={{
                      padding: "10px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      marginLeft: "10px",
                    }}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              )}
              {currentData && currentData.length > 0 ? (
                <div style={{ marginTop: "10px", marginLeft: "-340px" }}>
                  <div className={styles.tableContainers}>
                    <table className={styles.adminTable}>
                      <thead>
                        <tr>
                          <th
                            style={{ color: "white", backgroundColor: "grey", width: '8%' }}>
                            S.No
                          </th>
                          <th
                            style={{ color: "white", backgroundColor: "grey" }}>
                            Digital ID
                          </th>
                          <th
                            style={{ color: "white", backgroundColor: "grey" }}>
                            Mobile Number
                          </th>
                          <th
                            style={{ color: "white", backgroundColor: "grey" }}>
                            Status
                          </th>
                          <th
                            style={{ color: "white", backgroundColor: "grey" }}>
                            Changed At
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentData.map((data, index) => (
                          <tr
                            key={index}
                            ref={
                              index ===
                                Math.min(rowsPerPage, currentData.length) - 1
                                ? lastRowRef
                                : null
                            }>
                            <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                            <td>{data.digitalId}</td>
                            <td>{data.mobileNumber}</td>
                            <td>{data.customerStatus}</td>
                            <td >{`${data.changedAt?.split("T")[0]} ${data.changedAt?.split("T")[1]}`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {currentData.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginTop: "50px",
                      }}>
                      <Select
                        value={selectedDownload}
                        onChange={(e) => setSelectedDownload(e.target.value)}
                        displayEmpty
                        variant="outlined"
                        style={{
                          backgroundColor: "white",
                          color: "black",
                          width: "150px",
                          cursor: "pointer",
                          height: '38px'
                        }}>
                        <MenuItem value="" disabled>
                          Select Format
                        </MenuItem>
                        <MenuItem value="pdf">PDF</MenuItem>
                        <MenuItem value="excel">Excel</MenuItem>
                      </Select>
                      <Button
                        variant="contained"
                        color="primary"
                        style={{
                          backgroundColor: "red",
                          color: "white",
                          padding: "10px 5px",
                          fontSize: "10px",
                          width: "150px",
                          cursor: "pointer",
                          top: "0px",
                        }}
                        onClick={handleDownload}>
                        DOWNLOAD
                      </Button>
                    </div>
                  )}

                  {currentData.length > 0 && (
                    <div className={styles.paginationControlsHistory}>
                      <Button
                        variant="contained"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange("prev")}
                        sx={{ textTransform: "none" }}>
                        Previous
                      </Button>
                      <span>
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="contained"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange("next")}
                        sx={{ textTransform: "none" }}>
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    marginTop: "10px",
                    marginLeft: "-340px",
                    textAlign: "center",
                  }}>
                  {/* <h3>No ID History records found</h3> */}
                </div>
              )}

              {/* No Records Found Message */}
              {isSearchClicked &&
                totalPages === 0 &&
                currentData?.length === 0 && (
                  <div
                    style={{
                      marginTop: "10px",
                      marginLeft: "-340px",
                      textAlign: "center",
                    }}>
                    <h3>No Mobile number updation records found</h3>
                  </div>
                )}
            </>
          )}
        </div>

        <button className={styles.logoutButton} onClick={handleLogout}>
          <img
            src={logoutIcon}
            alt="Logout"
            className={styles.logoutIcon}
            title="Logout"
          />
        </button>

        {showConfirmationModal && (
          <LogoutConfirmation
            isOpen={showConfirmationModal}
            message="Are you sure you want to logout?"
            onConfirm={handleConfirmLogout}
            onClose={handleCancelLogout}
          />
        )}
        <SubmissionStatusModal
          open={modalOpen}
          onClose={handleCloseModal}
          isSuccess={isSuccess}
          message={message}
        />
      </div>
    </div>
  );
};

export default MobileNumberupdationList;
